import React from 'react';
import { useLocation } from 'react-router-dom';
import { CommunityScopeSelector } from '@/components/admin/CommunityScopeSelector';
import { useCommunityScope } from '@/hooks/useCommunityScope';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Building2, MapPin } from 'lucide-react';

export const AdminCommunityScopeBar: React.FC = () => {
  const { currentUser } = useAuth();
  const { dataLoadStates } = useData();
  const {
    availableCommunities,
    selectedCommunityIds,
    setSelectedCommunityIds,
    canChangeCommunity,
    isGlobalAdmin,
    activeCommunity,
  } = useCommunityScope();
  const location = useLocation();

  const selectedCommunities = React.useMemo(() => {
    if (!selectedCommunityIds.length) {
      return [];
    }

    const selectedIds = new Set(selectedCommunityIds);
    return availableCommunities.filter(community => selectedIds.has(community.id));
  }, [availableCommunities, selectedCommunityIds]);

  const shouldHideForRoute = React.useMemo(() => {
    const pathname = location.pathname ?? '';
    return (
      pathname.startsWith('/app/admin/dashboard') ||
      pathname.startsWith('/app/documents') ||
      pathname.startsWith('/app/suggestions') ||
      pathname.startsWith('/app/users')
    );
  }, [location.pathname]);

  const handleChange = React.useCallback((communityIds: string[]) => {
    setSelectedCommunityIds(communityIds);
  }, [setSelectedCommunityIds]);

  const hasMultipleSelections = selectedCommunityIds.length > 1;
  const selectionTitle = hasMultipleSelections
    ? `${selectedCommunityIds.length} comunidades seleccionadas`
    : activeCommunity?.name ?? 'Todas las comunidades';

  if (currentUser?.role !== 'admin' || !isGlobalAdmin || shouldHideForRoute) {
    return null;
  }

  if (dataLoadStates.communities === 'loading' || dataLoadStates.communities === 'idle') {
    return null;
  }

  if (availableCommunities.length === 0) {
    return (
      <Card className="w-full border-dashed bg-muted/30 shadow-none" data-onboarding="community-scope">
        <div className="flex flex-col gap-1 px-4 py-3 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Sin comunidades disponibles</p>
          <p>Crea una comunidad para empezar a gestionar sus servicios y residentes.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full bg-app-surface/80 shadow-none" data-onboarding="community-scope">
      <div className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center md:gap-3">
          <Badge variant="outline" className="w-fit bg-primary/5 text-primary">
            Contexto de trabajo
          </Badge>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{selectionTitle}</p>
            {hasMultipleSelections ? (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {selectedCommunities.slice(0, 4).map(community => (
                  <Badge key={community.id} variant="secondary" className="bg-muted text-muted-foreground">
                    {community.name}
                  </Badge>
                ))}
                {selectedCommunities.length > 4 ? (
                  <Badge variant="secondary" className="bg-muted text-muted-foreground">
                    +{selectedCommunities.length - 4}
                  </Badge>
                ) : null}
              </div>
            ) : activeCommunity ? (
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" />
                  {activeCommunity.address}
                </span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {activeCommunity.city}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {activeCommunity.members.length} residentes
                </span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Mostrando datos agregados de todas las comunidades.
              </p>
            )}
          </div>
        </div>
        <CommunityScopeSelector
          communities={availableCommunities}
          value={selectedCommunityIds}
          onChange={handleChange}
          disabled={!canChangeCommunity}
          className="md:w-auto md:flex-none"
          triggerClassName="md:w-[280px]"
          singleSelect
          allowAllOption
          allOptionLabel="Todas las comunidades"
        />
      </div>
    </Card>
  );
};
